<script setup lang="ts">
import DefaultLayout from '~/components/layout/default.vue';
import schemaMarkup from 'assets/data/schema-markup';
import Maintenance from '~/components/user-interface/maintenance.vue';
import SuspendedOperations from '~/components/user-interface/suspended-operations.vue';
import useGetPage from '~/composables/useGetPage';
import type { IGameCategoriesV4Data } from '~/interfaces/dto/games/response';
import { useGetVertical } from '~/composables/useGaming';
import { globalKeys } from 'assets/data/global-keys';
import { useLocale } from '~/composables/useStates/useLocale';

const route = useRoute();
const { $config } = useNuxtApp();
const siteStore = useSiteStore();
const page = useGetPage({ setSeoMeta: false });
const vertical = ref<IGameCategoriesV4Data>({
  categories: [],
  providers: [],
  themes: [],
  loading: true,
});
const loading = ref<boolean>(true);
const locale = useLocale();
const isDemoPlay = computed(() =>
  route.path.includes(globalKeys.navigationKeys.demoPlayRoute),
);

onMounted(() => (loading.value = false));

useHead({
  script: [
    {
      innerHTML: `window.fbAsyncInit = function () { window.FB.init({ appId: ${$config.public.metaAppId}, xfbml: false, version: 'v25.0', status: true, })};`,
      async: true,
      defer: true,
      tagPosition: 'bodyOpen',
    },
    {
      src: `https://connect.facebook.net/${locale.value?.locale?.replace('-', '_') || 'en_US'}/sdk.js`,
      async: true,
      crossorigin: 'anonymous',
      defer: true,
      tagPosition: 'bodyOpen',
    },
    {
      innerHTML: schemaMarkup[siteStore.getRegionCode],
      type: 'application/ld+json',
    },
  ],
});

const inMaintenance = computed(
  () => siteStore.featureFlags.get('website.enablemaintenancepage') || false,
);
const inSuspense = computed(
  () => siteStore.featureFlags.get('website.enablesuspendedpage') || false,
);

const siteDisabled = computed(() => inMaintenance.value || inSuspense.value);

watch(
  () => route.path,
  () => {
    useGetPage({
      path: route.path,
      setSeoMeta: true,
    });
  },
);

watch(
  () => [page.value?.Mapping, isDemoPlay.value],
  async (_key, _oldKey, onCleanup) => {
    let cancelled = false;
    onCleanup(() => (cancelled = true));

    vertical.value.loading = true;
    const data = await useGetVertical(
      isDemoPlay.value ? { freePlayOnly: true, count: 100 } : undefined,
    );
    if (!cancelled) vertical.value = data;
  },
  { immediate: true },
);

provide<Ref<IGameCategoriesV4Data>>('vertical', vertical);
</script>
<template>
  <DefaultLayout v-if="!siteStore.isWebview && !inMaintenance">
    <LazyUserInterfaceGenericLoader
      v-if="siteStore.isFullscreenLoading ?? vertical?.loading"
      full-screen
    />
    <SuspendedOperations v-if="inSuspense" />
    <NuxtPage v-else />
  </DefaultLayout>
  <NuxtPage v-if="siteStore.isWebview && !siteDisabled" />
  <Maintenance v-if="inMaintenance" />
</template>
